import { DataGrid, GridActionsCellItem, GridCellParams, GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import TableLanguagesCell from '../../../../../shared/components/table/TableLanguagesCell'
import TableTagsCell from '../../../../../shared/components/table/TableTagsCell'
import TableTitleCell from '../../../../../shared/components/table/TableTitleCell'
import TableToolbar from '../../../../../shared/components/table/TableToolbar'

interface NewsTableProps {
  news: any[]
  isMobile: boolean
  loading: boolean
  globalBrandId: any
  onView: (params: GridCellParams) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onHighlight: (id: number, isHighlight: boolean) => void
}

export const NewsTable = ({ news, loading, isMobile, globalBrandId, onView, onEdit, onDelete, onHighlight }: NewsTableProps) => {
  const { t } = useTranslation(['global'])

  const getColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: t('title'),
        flex: 1,
        renderCell: (params: GridCellParams) => (
          <TableTitleCell
            params={params}
            caseFirst="primary"
            caseSecond="secondary"
            caseThird="grey"
            caseDefault="grey"
            onHighlight={onHighlight}
          />
        )
      },
      {
        field: 'tags',
        headerName: '',
        width: 90,
        maxWidth: 240,
        hideable: isMobile,
        renderCell: (params: GridCellParams) => <TableTagsCell params={params} />
      },
      { field: 'id', headerName: 'Id', width: 70, hideable: isMobile },
      {
        field: 'brand',
        headerName: t('brand'),
        width: 90,
        maxWidth: 120,
        hideable: globalBrandId !== null,
        valueGetter: (params: GridCellParams) => params.row.brand?.name || '-'
      },
      {
        field: 'language',
        headerName: t('language'),
        width: 120,
        hideable: isMobile,
        renderCell: (params: GridCellParams) => <TableLanguagesCell params={params} />
      },
      {
        field: 'role',
        headerName: t('roles'),
        width: 120,
        hideable: isMobile,
        valueGetter: (params: GridCellParams) => params.row.role?.name || '-'
      },
      {
        field: 'created_at',
        headerName: t('date'),
        width: 150,
        hideable: isMobile,
        valueGetter: (params: GridCellParams) => {
          if (params.row.created_at !== null) {
            return dayjs(params.row.created_at).format('DD MMM YYYY')
          }
          return '--'
        }
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 90,
        getActions: (params: GridCellParams) => [
          <GridActionsCellItem
            label={t('view')}
            onClick={() => onView(params)}
            showInMenu
          />,
          <GridActionsCellItem
            label={t('edit')}
            onClick={() => onEdit(params.id as number)}
            showInMenu
          />,
          <GridActionsCellItem
            label={t('delete')}
            onClick={() => onDelete(params.id as number)}
            showInMenu
          />
        ]
      }
    ].filter(columns => !(isMobile && ['id', 'tags', 'language', 'role', 'created_at'].includes(columns.field)))
  }

  const columns = getColumns()

  return (
    <>
      <DataGrid
        rows={news}
        columns={columns}
        pageSizeOptions={[10, 20, 30]}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 }
          }
        }}
        autoHeight
        density="comfortable"
        slots={{
          toolbar: TableToolbar
        }}
        disableColumnMenu
        disableRowSelectionOnClick
      />
    </>
  )
}
